import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { useTheme } from '../../contexts/ThemeContext';
import { AnalyticsData } from '../../types/admin';
import { adminApi } from '../../services/admin/adminApi';

const COLORS = ['#22c55e', '#14b8a6', '#0ea5e9', '#8b5cf6', '#6b7280'];

const Analytics: React.FC = () => {
  const { isDark } = useTheme();
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);

  useEffect(() => {
    fetchAnalytics();
  }, [days]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const result = await adminApi.getAnalytics(days);
      setData(result);
    } catch (err) {
      // Mock data
      setData({
        messagesPerDay: [
          { date: '2025-12-18', day: 'Qua', count: 45 },
          { date: '2025-12-19', day: 'Qui', count: 52 },
          { date: '2025-12-20', day: 'Sex', count: 38 },
          { date: '2025-12-21', day: 'Sab', count: 20 },
          { date: '2025-12-22', day: 'Dom', count: 15 },
          { date: '2025-12-23', day: 'Seg', count: 58 },
          { date: '2025-12-24', day: 'Ter', count: 42 },
        ],
        requestTypes: [
          { type: 'Nova Cotacao', count: 120, percentage: 40 },
          { type: 'Renovacao', count: 75, percentage: 25 },
          { type: 'Consulta', count: 60, percentage: 20 },
          { type: 'Suporte', count: 30, percentage: 10 },
          { type: 'Outros', count: 15, percentage: 5 },
        ],
        totalMessages: 300,
        averageResponseTime: 2.5,
        conversionRate: 35,
        funnel: { created: 0, priced: 0, proposed: 0, closeRate: 0 },
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Analytics
          </h1>
          <p className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            Metricas e estatisticas do sistema
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className={`px-4 py-2 rounded-lg border ${
              isDark
                ? 'bg-gray-700 border-gray-600 text-white'
                : 'bg-white border-gray-300 text-gray-900'
            } focus:ring-2 focus:ring-green-500`}
          >
            <option value={7}>Ultimos 7 dias</option>
            <option value={14}>Ultimos 14 dias</option>
            <option value={30}>Ultimos 30 dias</option>
          </select>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className={`rounded-lg shadow-sm ${isDark ? 'bg-gray-800' : 'bg-white'} p-6`}>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Total de Mensagens</p>
          <p className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {data?.totalMessages || 0}
          </p>
        </div>
        <div className={`rounded-lg shadow-sm ${isDark ? 'bg-gray-800' : 'bg-white'} p-6`}>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Tempo Medio de Resposta</p>
          <p className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {data?.averageResponseTime || 0}s
          </p>
        </div>
        <div className={`rounded-lg shadow-sm ${isDark ? 'bg-gray-800' : 'bg-white'} p-6`}>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Taxa de Conversao</p>
          <p className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {data?.conversionRate || 0}%
          </p>
        </div>
      </div>

      {/* Funil de Fechamento (cotam vs contratam) */}
      <div>
        <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`}>
          Funil de Fechamento (cotam vs contratam)
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className={`rounded-lg shadow-sm ${isDark ? 'bg-gray-800' : 'bg-white'} p-6`}>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Cotacoes criadas</p>
            <p className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {data?.funnel?.created ?? 0}
            </p>
          </div>
          <div className={`rounded-lg shadow-sm ${isDark ? 'bg-gray-800' : 'bg-white'} p-6`}>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Precificadas</p>
            <p className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {data?.funnel?.priced ?? 0}
            </p>
          </div>
          <div className={`rounded-lg shadow-sm ${isDark ? 'bg-gray-800' : 'bg-white'} p-6`}>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Propostas (fechamentos)</p>
            <p className="text-3xl font-bold text-green-600">
              {data?.funnel?.proposed ?? 0}
            </p>
          </div>
          <div className={`rounded-lg shadow-sm ${isDark ? 'bg-gray-800' : 'bg-white'} p-6`}>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Taxa de fechamento</p>
            <p className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {data?.funnel?.closeRate ?? 0}%
            </p>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Messages per Day */}
        <div className={`rounded-lg shadow-sm ${isDark ? 'bg-gray-800' : 'bg-white'} p-6`}>
          <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`}>
            Mensagens por Dia
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data?.messagesPerDay || []}>
                <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#e5e7eb'} />
                <XAxis dataKey="day" stroke={isDark ? '#9ca3af' : '#6b7280'} />
                <YAxis stroke={isDark ? '#9ca3af' : '#6b7280'} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: isDark ? '#1f2937' : '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                  }}
                />
                <Bar dataKey="count" fill="#22c55e" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Request Types */}
        <div className={`rounded-lg shadow-sm ${isDark ? 'bg-gray-800' : 'bg-white'} p-6`}>
          <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`}>
            Tipos de Solicitacao
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={(data?.requestTypes || []) as any[]}
                  dataKey="count"
                  nameKey="type"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label={({ name, percent }: { name?: string; percent?: number }) => `${name || ''}: ${((percent || 0) * 100).toFixed(0)}%`}
                  labelLine={false}
                >
                  {(data?.requestTypes || []).map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: isDark ? '#1f2937' : '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                  }}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
